import { useState, useCallback, useEffect, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { ShareIcon } from '@heroicons/react/24/outline';
import YouTubeIcon from '../../../public/images/youtube_shorts_icon.svg';
import InstagramIcon from '../../../public/images/instagram_icon.svg';
import XIcon from '../../../public/images/x_icon_dark.svg';
import TikTokIcon from '../../../public/images/tiktok_icon.svg';
import RedditIcon from '../../../public/images/reddit_icon.svg';
import OnlyFansIcon from '../../../public/images/onlyfans_icon.svg';
import FanslyIcon from '../../../public/images/fansly_icon.svg';
import FacebookIcon from '../../../public/images/facebook_icon.svg';

interface SocialOption {
  title: string;
  iconSrc: string;
  disabled: boolean;
}

const socialOptions: SocialOption[] = [
  { title: 'YouTube', iconSrc: YouTubeIcon, disabled: true },
  { title: 'Instagram', iconSrc: InstagramIcon, disabled: true },
  { title: 'X', iconSrc: XIcon, disabled: true },
  { title: 'TikTok', iconSrc: TikTokIcon, disabled: true },
  { title: 'Reddit', iconSrc: RedditIcon, disabled: true },
  // { title: 'OnlyFans', iconSrc: OnlyFansIcon, disabled: true },
  // { title: 'Fansly', iconSrc: FanslyIcon, disabled: true },
  { title: 'Facebook', iconSrc: FacebookIcon, disabled: true },
];

interface SocialShareButtonProps {
  url: string;
}

export default function SocialShareButton({ url }: SocialShareButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  const handleResize = useCallback(() => {
    setIsMobile(window.innerWidth <= 768); // Use 768px as breakpoint for mobile
  }, []);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  }, []);

  const updatePosition = useCallback(() => {
    if (buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - buttonRect.bottom;

      // Set to open upward if there is less than 300px space below
      setOpenUpward(spaceBelow < 300);
    }
  }, []);

  useEffect(() => {
    handleResize();
    updatePosition();
    window.addEventListener('resize', handleResize);
    window.addEventListener('resize', updatePosition);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('resize', updatePosition);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleResize, updatePosition, handleClickOutside]);

  return (
    <div className="relative inline-block" ref={buttonRef}>
      <button
        onClick={() => {
          setIsOpen(!isOpen);
          updatePosition(); // Recalculate position on open
        }}
        className="inline-flex items-center rounded-md bg-white/80 backdrop-blur-sm px-2.5 py-1.5 text-sm
          font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300
          hover:bg-gray-50 transition-colors"
      >
        <ShareIcon className="h-5 w-5" />
      </button>

      {isOpen && (
        <div
          className={`${
            isMobile
              ? 'fixed inset-x-0 bottom-0 z-50 w-full rounded-t-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none'
              : `${openUpward ? 'bottom-full mb-2' : 'top-full mt-2'} absolute right-0 z-50 w-64 origin-top-right rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none`
          }`}
        >
          {isMobile && (
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <h3 className="text-sm font-medium text-gray-900">Share to</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                <span className="sr-only">Close</span>
              </button>
            </div>
          )}

          {!isMobile && (
            <div className="px-4 py-3 border-b border-gray-100">
              <h3 className="text-sm font-medium text-gray-900">Share to</h3>
            </div>
          )}

          <div className="max-h-96 overflow-auto py-2">
            {socialOptions.map((option) => (
              <div
                key={option.title}
                className="px-4 py-3 hover:bg-gray-50/50 transition-colors cursor-default"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img
                      src={option.iconSrc}
                      alt={`${option.title} icon`}
                      className="h-5 w-5 text-gray-400 object-contain"
                    />
                    <span className="text-sm text-gray-700">{option.title}</span>
                  </div>
                  <span className="text-xs text-indigo-500 font-medium bg-indigo-50 px-2 py-0.5 rounded">
                    Coming soon
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="px-4 py-3 text-xs text-center text-gray-500">
            More platforms coming soon
          </div>
        </div>
      )}
    </div>
  );
}
